import React from 'react';
import { AlertTriangle, BarChart3, Edit, Users } from 'lucide-react';
import { estDemiGroupe, estDemiGroupeVide } from '../../utils/groupesDeBesoin';

const CardClasse = ({ classItem, percentages, onRowClick, index }) => {
  // Vérifier si on a des données de compétences
  const hasCompetenceData = percentages !== 0 &&
    percentages &&
    Object.values(percentages).some(val => val > 0);

  // Extraire le niveau de la classe (6, 5, 4, ou 3)
  // Pour les groupes, utiliser le premier caractère de la première classe d'origine
  const classLevel = classItem.classes_detail && classItem.classes_detail.length > 0
    ? classItem.classes_detail[0].charAt(0)
    : classItem.nom?.charAt(0);

  // Un DEMI-GROUPE (une seule classe, créé par l'ENT) se compose par ses profs
  // eux-mêmes : on y entre toujours, même vide (décision prof 2026-09-30). Seul
  // un groupe de besoin vide reste fermé : le coordo ou le délégué l'importe.
  // Champ absent (back plus ancien) : groupe de besoin, comportement d'avant.
  const demiGroupe = estDemiGroupe(classItem);
  // Demi-groupe encore vide : on le compose d'abord (page Élèves, liste à cocher).
  const demiGroupeVide = estDemiGroupeVide(classItem);

  // Déterminer si c'est un groupe vide (non cliquable)
  const isEmptyGroup = (classItem.nombre_eleves === 0 || classItem.nombre_eleves === undefined) &&
                        classItem.classes_detail &&
                        classItem.classes_detail.length > 0 &&
                        !demiGroupe;

  // Définir les couleurs selon le niveau
  const colorSchemes = {
    '6': {
      base: 'from-blue-600 via-cyan-500 to-sky-400',
      blob1: ['#67e8f9', '#22d3ee', '#0ea5e9'],
      blob2: ['#a5f3fc', '#06b6d4'],
      blob3: ['#ecfeff', '#67e8f9'],
      blob4: ['#38bdf8', '#0284c7']
    },
    '5': {
      base: 'from-purple-600 via-violet-500 to-indigo-400',
      blob1: ['#c4b5fd', '#a78bfa', '#8b5cf6'],
      blob2: ['#ddd6fe', '#7c3aed'],
      blob3: ['#ede9fe', '#c4b5fd'],
      blob4: ['#a78bfa', '#6d28d9']
    },
    '4': {
      base: 'from-emerald-600 via-teal-500 to-cyan-400',
      blob1: ['#5eead4', '#2dd4bf', '#14b8a6'],
      blob2: ['#99f6e4', '#0d9488'],
      blob3: ['#ccfbf1', '#5eead4'],
      blob4: ['#2dd4bf', '#0f766e']
    },
    '3': {
      base: 'from-rose-600 via-pink-500 to-fuchsia-400',
      blob1: ['#f9a8d4', '#f472b6', '#ec4899'],
      blob2: ['#fce7f3', '#db2777'],
      blob3: ['#fdf2f8', '#f9a8d4'],
      blob4: ['#f472b6', '#be185d']
    }
  };

  const colors = colorSchemes[classLevel] || colorSchemes['6']; // Default to blue if not found

  // Logique d'affichage du nombre d'élèves pour établissements GAR
  const getElevesDisplay = () => {
    const nbMaths = classItem.nombre_eleves_maths;
    const nbFrancais = classItem.nombre_eleves_francais;
    const nbTotal = classItem.nombre_eleves;

    // Si les deux existent
    if (nbMaths !== undefined && nbMaths !== null && nbFrancais !== undefined && nbFrancais !== null) {
      // Si égaux, afficher un seul nombre
      if (nbMaths === nbFrancais) {
        return { text: `${nbMaths} élèves`, isSplit: false };
      }
      // Si différents, afficher les deux
      return { text: `${nbMaths} M / ${nbFrancais} F`, isSplit: true };
    }
    // Si un seul existe
    if (nbMaths !== undefined && nbMaths !== null) {
      return { text: `${nbMaths} élèves`, isSplit: false };
    }
    if (nbFrancais !== undefined && nbFrancais !== null) {
      return { text: `${nbFrancais} élèves`, isSplit: false };
    }
    // Sinon afficher nombre_eleves
    return { text: `${nbTotal} élèves`, isSplit: false };
  };

  const elevesDisplay = getElevesDisplay();

  return (
    <div
      className={`group bg-white/80 backdrop-blur-md rounded-3xl shadow-xl shadow-slate-300/60 overflow-hidden transition-all duration-300 h-fit border border-sky-100 ${
        isEmptyGroup
          ? 'cursor-default opacity-80'
          : 'cursor-pointer hover:shadow-2xl hover:shadow-slate-400/70 transform hover:scale-[1.02]'
      }`}
      onClick={isEmptyGroup ? undefined : () => onRowClick(classItem)}>

      {/* ============================================ */}
      {/* HEADER - Enhanced Liquid Morphing */}
      {/* ============================================ */}
      <div className="relative overflow-hidden min-h-[160px] xl:min-h-[130px] 2xl:min-h-[145px] 3xl:min-h-[160px]">
        {/* Base gradient - more vibrant */}
        <div className={`absolute inset-0 bg-gradient-to-br ${colors.base}`} />

        {/* Multiple animated liquid blobs - more movement */}
        <svg className="absolute inset-0 w-full h-full" viewBox="0 0 400 200" preserveAspectRatio="none">
          <defs>
            {/* Gradient 1 - Large blob */}
            <linearGradient id={`immersive-blob1-${index}`} x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" stopColor={colors.blob1[0]} stopOpacity="0.8" />
              <stop offset="50%" stopColor={colors.blob1[1]} stopOpacity="0.5" />
              <stop offset="100%" stopColor={colors.blob1[2]} stopOpacity="0.3" />
            </linearGradient>

            {/* Gradient 2 - Medium blob */}
            <linearGradient id={`immersive-blob2-${index}`} x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" stopColor={colors.blob2[0]} stopOpacity="0.7" />
              <stop offset="100%" stopColor={colors.blob2[1]} stopOpacity="0.3" />
            </linearGradient>

            {/* Gradient 3 - Small accent */}
            <linearGradient id={`immersive-blob3-${index}`} x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" stopColor={colors.blob3[0]} stopOpacity="0.6" />
              <stop offset="100%" stopColor={colors.blob3[1]} stopOpacity="0.2" />
            </linearGradient>

            {/* Gradient 4 - Extra blob */}
            <linearGradient id={`immersive-blob4-${index}`} x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" stopColor={colors.blob4[0]} stopOpacity="0.6" />
              <stop offset="100%" stopColor={colors.blob4[1]} stopOpacity="0.2" />
            </linearGradient>

            {/* Radial glow */}
            <radialGradient id={`immersive-glow-${index}`} cx="50%" cy="50%" r="50%">
              <stop offset="0%" stopColor="#ffffff" stopOpacity="0.3" />
              <stop offset="100%" stopColor="#ffffff" stopOpacity="0" />
            </radialGradient>
          </defs>

          {/* Blob 1 - Large, slow movement */}
          <path fill={`url(#immersive-blob1-${index})`}>
            <animate
              attributeName="d"
              dur="6s"
              repeatCount="indefinite"
              values="
                M30,80 Q80,20 150,60 Q220,100 200,160 Q180,220 100,180 Q20,140 30,80;
                M50,60 Q120,10 180,50 Q240,90 220,160 Q200,230 110,190 Q30,150 50,60;
                M40,90 Q90,30 160,70 Q230,110 210,170 Q190,230 100,190 Q20,150 40,90;
                M30,80 Q80,20 150,60 Q220,100 200,160 Q180,220 100,180 Q20,140 30,80
              "
            />
          </path>

          {/* Blob 2 - Right side, medium speed */}
          <path fill={`url(#immersive-blob2-${index})`}>
            <animate
              attributeName="d"
              dur="5s"
              repeatCount="indefinite"
              values="
                M280,30 Q350,60 360,120 Q370,180 310,170 Q250,160 260,100 Q270,40 280,30;
                M300,50 Q370,80 380,140 Q390,200 330,190 Q270,180 280,120 Q290,60 300,50;
                M270,40 Q340,70 350,130 Q360,190 300,180 Q240,170 250,110 Q260,50 270,40;
                M280,30 Q350,60 360,120 Q370,180 310,170 Q250,160 260,100 Q270,40 280,30
              "
            />
          </path>

          {/* Blob 3 - Small, fast movement */}
          <path fill={`url(#immersive-blob3-${index})`}>
            <animate
              attributeName="d"
              dur="4s"
              repeatCount="indefinite"
              values="
                M380,10 Q410,30 400,70 Q390,110 350,100 Q310,90 330,50 Q350,10 380,10;
                M360,20 Q390,40 380,80 Q370,120 330,110 Q290,100 310,60 Q330,20 360,20;
                M390,15 Q420,35 410,75 Q400,115 360,105 Q320,95 340,55 Q360,15 390,15;
                M380,10 Q410,30 400,70 Q390,110 350,100 Q310,90 330,50 Q350,10 380,10
              "
            />
          </path>

          {/* Blob 4 - Bottom left, wave motion */}
          <path fill={`url(#immersive-blob4-${index})`}>
            <animate
              attributeName="d"
              dur="7s"
              repeatCount="indefinite"
              values="
                M0,120 Q40,100 80,130 Q120,160 100,200 Q80,240 40,220 Q0,200 0,120;
                M0,140 Q50,110 100,140 Q150,170 130,210 Q110,250 50,230 Q0,210 0,140;
                M0,130 Q30,90 70,120 Q110,150 90,200 Q70,250 30,220 Q0,190 0,130;
                M0,120 Q40,100 80,130 Q120,160 100,200 Q80,240 40,220 Q0,200 0,120
              "
            />
          </path>

          {/* Floating particles */}
          <circle cx="100" cy="50" r="3" fill="white" opacity="0.4">
            <animate attributeName="cy" dur="3s" values="50;30;50" repeatCount="indefinite" />
            <animate attributeName="opacity" dur="3s" values="0.4;0.8;0.4" repeatCount="indefinite" />
          </circle>
          <circle cx="300" cy="80" r="2" fill="white" opacity="0.3">
            <animate attributeName="cy" dur="4s" values="80;50;80" repeatCount="indefinite" />
            <animate attributeName="opacity" dur="4s" values="0.3;0.7;0.3" repeatCount="indefinite" />
          </circle>
          <circle cx="200" cy="120" r="2.5" fill="white" opacity="0.35">
            <animate attributeName="cy" dur="3.5s" values="120;90;120" repeatCount="indefinite" />
          </circle>

          {/* Glow effect */}
          <ellipse cx="200" cy="100" rx="150" ry="80" fill={`url(#immersive-glow-${index})`}>
            <animate attributeName="rx" dur="4s" values="150;180;150" repeatCount="indefinite" />
            <animate attributeName="ry" dur="4s" values="80;100;80" repeatCount="indefinite" />
          </ellipse>
        </svg>

        {/* Light overlay gradient */}
        <div className="absolute inset-0 bg-gradient-to-b from-white/20 via-transparent to-black/20" />

        {/* Shimmer effect */}
        <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-1000" />

        {/* Content layer */}
        <div className="relative p-5 xl:p-3.5 2xl:p-4 3xl:p-5 pb-6 xl:pb-4 2xl:pb-5 3xl:pb-6">
          <div className="flex items-center gap-2 xl:gap-1.5 2xl:gap-1.5 3xl:gap-2 mb-3 xl:mb-2 2xl:mb-2.5 3xl:mb-3">
            <span className="px-2 xl:px-1.5 2xl:px-1.5 3xl:px-2 py-0.5 bg-white/20 backdrop-blur text-white text-xs xl:text-[10px] 2xl:text-[11px] 3xl:text-xs font-medium rounded-full">
              {classItem.annee_scolaire}
            </span>
            {classItem.classes_detail && classItem.classes_detail.length > 0 && (
              <span className="px-2 xl:px-1.5 2xl:px-1.5 3xl:px-2 py-0.5 bg-white/30 backdrop-blur text-white text-xs xl:text-[10px] 2xl:text-[11px] 3xl:text-xs font-medium rounded-full">
                {classItem.classes_detail.join(", ")}
              </span>
            )}
            {classItem.nombre_eleves === 0 && (
              <span className="px-2.5 xl:px-2 2xl:px-2 3xl:px-2.5 py-0.5 bg-orange-500 text-white text-xs xl:text-[10px] 2xl:text-[11px] 3xl:text-xs font-bold rounded-full flex items-center gap-1">
                <AlertTriangle size={12} className="xl:w-2.5 xl:h-2.5 2xl:w-3 2xl:h-3 3xl:w-3 3xl:h-3" />
                Vide
              </span>
            )}
          </div>

          <div className="flex items-center justify-between">
            <div className="flex-1">
              <h3 className="text-2xl xl:text-lg 2xl:text-xl 3xl:text-2xl font-bold text-white mb-2 xl:mb-1.5 2xl:mb-1.5 3xl:mb-2">{classItem.nom}</h3>
              <div className="flex items-center gap-1.5 px-2.5 xl:px-2 2xl:px-2 3xl:px-2.5 py-1 bg-white/20 backdrop-blur rounded-lg w-fit">
                <Users size={16} className="text-white xl:w-3.5 xl:h-3.5 2xl:w-[15px] 2xl:h-[15px] 3xl:w-4 3xl:h-4" />
                <span className={`font-semibold text-white ${elevesDisplay.isSplit ? 'text-xs xl:text-[10px] 2xl:text-[11px] 3xl:text-xs' : 'text-sm xl:text-xs 2xl:text-xs 3xl:text-sm'}`}>
                  {elevesDisplay.text}
                </span>
              </div>
            </div>

            <div className="w-20 h-20 xl:w-16 xl:h-16 2xl:w-[70px] 2xl:h-[70px] 3xl:w-20 3xl:h-20 bg-white/30 backdrop-blur-md rounded-2xl flex flex-col items-center justify-center border border-white/20 shadow-lg">
              <span className="text-3xl xl:text-2xl 2xl:text-2xl 3xl:text-3xl font-bold text-white">
                {classItem.moyenne_classe ? parseFloat(classItem.moyenne_classe.toFixed(1)) : "-"}
              </span>
              <span className="text-xs xl:text-[10px] 2xl:text-[11px] 3xl:text-xs text-white font-medium">Moyenne</span>
            </div>
          </div>
        </div>
      </div>

      <div className="p-5 xl:p-3.5 2xl:p-4 3xl:p-5 pt-4 xl:pt-3 2xl:pt-3.5 3xl:pt-4 bg-white/40 backdrop-blur-sm">
        {/* Competence breakdown */}
        {hasCompetenceData ? (
          <div className="mb-5 xl:mb-3 2xl:mb-4 3xl:mb-5">
            {/* Grille 2 colonnes pour les 6 premières compétences */}
            <div className="grid grid-cols-2 gap-3 xl:gap-2 2xl:gap-2.5 3xl:gap-3 mb-3 xl:mb-2 2xl:mb-2.5 3xl:mb-3">
              {[
                { label: 'Très bon niveau', percentage: (percentages.nb_vert_plus * 100).toFixed(0), color: 'bg-green-800', lightColor: 'bg-green-100' },
                { label: 'Acquis', percentage: (percentages.nb_vert * 100).toFixed(0), color: 'bg-green-500', lightColor: 'bg-green-50' },
                { label: 'En cours', percentage: (percentages.nb_vert_pm * 100).toFixed(0), color: 'bg-teal-400', lightColor: 'bg-teal-50' },
                { label: 'Fragile', percentage: (percentages.nb_yellow * 100).toFixed(0), color: 'bg-yellow-400', lightColor: 'bg-yellow-50' },
                { label: 'À consolider', percentage: (percentages.nb_orange * 100).toFixed(0), color: 'bg-orange-500', lightColor: 'bg-orange-50' },
                { label: 'Non acquis', percentage: (percentages.nb_rouge * 100).toFixed(0), color: 'bg-red-500', lightColor: 'bg-red-50' },
              ].filter(item => parseFloat(item.percentage) > 0).map((item, i) => (
                <div key={i}>
                  <div className="flex items-center justify-between text-xs xl:text-[10px] 2xl:text-[11px] 3xl:text-xs mb-1">
                    <span className="text-slate-600 truncate">{item.label}</span>
                    <span className="font-semibold text-slate-800 ml-1">{item.percentage}%</span>
                  </div>
                  <div className={`h-1.5 xl:h-1 2xl:h-1 3xl:h-1.5 ${item.lightColor} rounded-full overflow-hidden`}>
                    <div
                      className={`h-full ${item.color} rounded-full transition-all duration-1000 ease-out`}
                      style={{ width: `${item.percentage}%` }}
                    />
                  </div>
                </div>
              ))}
            </div>

            {/* Sans score en pleine largeur */}
            {percentages.nb_no_scores > 0 && (
              <div>
                <div className="flex items-center justify-between text-xs mb-1">
                  <span className="text-slate-600">Sans score</span>
                  <span className="font-semibold text-slate-800">{(percentages.nb_no_scores * 100).toFixed(0)}%</span>
                </div>
                <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden">
                  <div
                    className="h-full bg-gray-400 rounded-full transition-all duration-1000 ease-out"
                    style={{ width: `${(percentages.nb_no_scores * 100).toFixed(0)}%` }}
                  />
                </div>
              </div>
            )}
          </div>
        ) : (
          <div className="mb-5 xl:mb-3 2xl:mb-4 3xl:mb-5 text-center py-8 xl:py-5 2xl:py-6 3xl:py-8">
            <p className="text-slate-400 text-sm xl:text-xs 2xl:text-xs 3xl:text-sm">Aucune donnée de compétences</p>
          </div>
        )}

        {/* Action buttons */}
        <div className="flex gap-3 xl:gap-2 2xl:gap-2.5 3xl:gap-3">
          {classItem.nombre_eleves > 1 || demiGroupe ? (
            <button
              className="flex-1 py-2.5 xl:py-2 2xl:py-2 3xl:py-2.5 bg-gradient-to-r from-blue-500 to-indigo-600 hover:from-blue-600 hover:to-indigo-700 text-white text-sm xl:text-xs 2xl:text-xs 3xl:text-sm font-medium rounded-lg transition-all flex items-center justify-center gap-2 shadow-lg shadow-blue-500/40 hover:shadow-xl hover:shadow-blue-600/50"
              onClick={(e) => {
                e.stopPropagation();
                onRowClick(classItem);
              }}>
              {demiGroupeVide ? (
                <Users size={16} className="xl:w-3.5 xl:h-3.5 2xl:w-[15px] 2xl:h-[15px] 3xl:w-4 3xl:h-4" />
              ) : (
                <BarChart3 size={16} className="xl:w-3.5 xl:h-3.5 2xl:w-[15px] 2xl:h-[15px] 3xl:w-4 3xl:h-4" />
              )}
              {demiGroupeVide ? 'Composer le demi-groupe' : 'Consulter'}
            </button>
          ) : (
            // Si c'est un groupe (a classes_detail), afficher un message au lieu d'un bouton
            classItem.classes_detail && classItem.classes_detail.length > 0 ? (
              <div className="flex-1 py-2.5 xl:py-2 2xl:py-2 3xl:py-2.5 bg-slate-100 text-slate-600 text-xs xl:text-[10px] 2xl:text-[11px] 3xl:text-xs font-medium rounded-lg flex items-center justify-center gap-2 border border-slate-200">
                <AlertTriangle size={14} />
                Contactez le coordonnateur de discipline
              </div>
            ) : (
              <button
                className="flex-1 py-2.5 xl:py-2 2xl:py-2 3xl:py-2.5 bg-gradient-to-r from-orange-500 to-amber-600 hover:from-orange-600 hover:to-amber-700 text-white text-sm xl:text-xs 2xl:text-xs 3xl:text-sm font-medium rounded-lg transition-all flex items-center justify-center gap-2 shadow-lg shadow-orange-500/40 hover:shadow-xl hover:shadow-orange-600/50"
                onClick={(e) => {
                  e.stopPropagation();
                  onRowClick(classItem);
                }}>
                <Edit size={16} />
                Importer des élèves
              </button>
            )
          )}
        </div>
      </div>
    </div>
  );
};

export default CardClasse;
